import React, { useCallback } from 'react';
import { Skeleton, Typography } from '@mui/material';
import moment from 'moment/moment';
import { Stack } from 'new/elements/Stack';
import styles from 'new/components/monitor/ResourceTriage/ResourceTriage.module.scss';
import { ResourceType } from 'new/components/monitor/ResourceTriage/components/ResourceType';
import { useResourceMeta } from 'new/hooks/adapters/useResourceMeta';
import {
  RESOURCE_TYPE_DATA_SET,
  RESOURCE_TYPE_FLOW,
  RESOURCE_TYPE_SCHEMA_SPECS,
  RESOURCE_TYPE_SINK,
  RESOURCE_TYPE_SOURCE,
} from 'js/constants/resourceTypes';
import DataSinkDetailActionsContainer from 'js/components/data_sinks/cards/DataSinkDetailActionsContainer';
import DataSourceDetailActionsContainer from 'js/components/data_sources/cards/DataSourceDetailActionsContainer';
import DataSetDetailActionsContainer from 'js/components/data_sets/cards/DataSetDetailActionsContainer';
import { useResource } from 'new/components/monitor/ResourceTriage/useResource';
import { Button } from 'new/elements/Button';
import { useModals } from 'new/hooks/useModals';
import { ModalFlowPreview } from 'new/elements/ModalFlowPreview';
import { ConnectionTypeChip } from 'new/elements/ConnectionTypeChip/ConnectionTypeChip';
import ViewExpandedPipelineButton from 'new/elements/ViewExpandedPipelineButton/ViewExpandedPipelineButton';
import PipelineStatusChip from 'new/components/pipelines/PipelineStatusChip/PipelineStatusChip';
import { RuntimeStatusIndicator } from 'new/elements/DotsLoader';
import { isCapsuleCluster } from 'js/lib/PipelineUtils';
import { CapsuleClusterChip } from 'new/modules/settings/capsules/components/CapsuleClusterChip/CapsuleClusterChip';
import { useUsers } from 'new/hooks/adapters/useUsers';
import { useOrgs } from 'new/hooks/adapters/useOrgs';

type ResumeProps = {
  name: string;
  loading?: boolean;
  data_source_id?: string;
  description?: string;
  rootClassName?: string;
  resourceType: string;
  is_detected: boolean;
  disableFlowSelection?: boolean;
  id: string;
  created_at?: string;
  data_credentials?: any;
  owner: any;
  onFlowItemClicked?: (type: string, data: any) => void;
};

const actions = {
  [RESOURCE_TYPE_SINK]: DataSinkDetailActionsContainer,
  [RESOURCE_TYPE_SOURCE]: DataSourceDetailActionsContainer,
  [RESOURCE_TYPE_DATA_SET]: DataSetDetailActionsContainer,
};

const selectActions = ['activate', 'activateFlow', 'pauseFlow'];
const selectFlowActions = [...selectActions.filter((action) => action !== 'activate')];

const resourcesShowOwnerOrg = [
  RESOURCE_TYPE_SOURCE,
  RESOURCE_TYPE_FLOW,
  RESOURCE_TYPE_DATA_SET,
  RESOURCE_TYPE_SINK,
  RESOURCE_TYPE_SCHEMA_SPECS,
];

const resourcesShowButton = [
  RESOURCE_TYPE_SOURCE,
  RESOURCE_TYPE_FLOW,
  RESOURCE_TYPE_DATA_SET,
  RESOURCE_TYPE_SINK,
];

const Loader = () => {
  return (
    <Stack padding="12px">
      <Stack className={styles.resumeSection}>
        <Stack marginBottom="10px" alignItems="flex-start" gap="16px">
          <Skeleton animation="wave" variant="rounded" width="90px" />
          <Skeleton animation="wave" variant="rounded" width="100px" />
        </Stack>
        <Stack
          marginTop="20px"
          marginBottom="12px"
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Skeleton animation="wave" variant="rounded" width="70%" />
        </Stack>
        <Skeleton animation="wave" variant="rounded" width="100%" />
        <Stack marginTop="32px" gap="8px" marginBottom="6px">
          <Skeleton animation="wave" variant="rounded" width="150px" />
          <Skeleton animation="wave" variant="rounded" width="85px" />
        </Stack>
      </Stack>
      <Stack
        className={styles.resumeSection}
        paddingTop="6px"
        marginBottom="16px"
        paddingBottom="6px"
      >
        <Stack gap="8px">
          <Skeleton animation="wave" variant="rounded" width="130px" />
          <Skeleton animation="wave" variant="rounded" width="230px" />
        </Stack>
      </Stack>
      <Skeleton animation="wave" variant="rounded" width="150px" height="40px" />
    </Stack>
  );
};

export const Resume = (props: ResumeProps) => {
  const {
    name,
    description,
    resourceType,
    id,
    created_at,
    data_credentials,
    rootClassName,
    onFlowItemClicked,
    disableFlowSelection,
    loading,
  } = props;
  const { resource } = useResource(resourceType, id);
  const { openModal, closeModal } = useModals();
  const connectionInfo = useResourceMeta(
    resourceType,
    resourceType === RESOURCE_TYPE_FLOW ? props.data_source_id : id,
    data_credentials,
  );
  const users = useUsers(true);
  const orgs = useOrgs();
  const owner = users[resource?.owner?.id || resource?.owner];

  const resourceOrg = orgs.find((org) => org.id === (resource?.org_id || resource?.org));
  const flowActionsOnly =
    resource?.node_type === 'api_result' || resource?.node_type === 'api_error';
  const ActionsComponent = actions[resourceType] || null;

  const onItemClick = useCallback(
    (type, data) => {
      closeModal(ModalFlowPreview.modalConfig.name);
      onFlowItemClicked(type, data);
    },
    [onFlowItemClicked],
  );

  const onShowModalFlowPreview = () => {
    openModal(ModalFlowPreview.modalConfig.name, {
      resourceType,
      disableFlowSelection,
      id,
      onItemClick,
    });
  };

  if (loading) {
    return <Loader />;
  }

  const resourceChipElement = (() => {
    // if the resource type is a nexset, don't render the duplicate connection type chip
    if (resourceType === RESOURCE_TYPE_DATA_SET) return null;

    // if resource has no connection info, don't render the connection type chip
    if (!connectionInfo?.iconSm || !connectionInfo?.name) return null;

    return (
      <div className={styles.resourceInfo}>
        <ConnectionTypeChip icon={connectionInfo?.iconSm} label={connectionInfo?.name} />
      </div>
    );
  })();

  return (
    <Stack className={rootClassName}>
      <Stack className={styles.resumeSection}>
        <Stack
          marginBottom="10px"
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          gap="8px"
        >
          <Stack gap="8px" alignItems="flex-start">
            <ResourceType type={resourceType} />
            {resourceChipElement}
            {isCapsuleCluster(resource) && <CapsuleClusterChip name={resource.cluster.name} />}
          </Stack>
          {resourceType === RESOURCE_TYPE_FLOW && resource && (
            <PipelineStatusChip
              id={id}
              status={resource.status}
              archivalStatus={resource.archival_status}
              showRuntimeIndicator={false}
            />
          )}
        </Stack>
        <Stack paddingTop="12px" direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="subtitle1" className={styles.resourceName}>
            {name}
          </Typography>
          {!!ActionsComponent && (
            <ActionsComponent
              data={resource}
              selectActions={flowActionsOnly ? selectFlowActions : selectActions}
              horizontalDots
              readOnly={resource?.readOnly}
              showStatus={false}
              showTransform={false}
              showSend={false}
              isManaged={resource?.isManaged}
              isDetected={resource?.isDetected}
              hideMenuDetails
            />
          )}
        </Stack>
        <Typography variant="body2" className={styles.resourceDesc}>
          {description || 'No description provided'}
        </Typography>
        <Stack marginTop="24px">
          <Stack>
            <Typography variant="subtitle2">Resource ID</Typography>
            <Typography variant="body2">{id}</Typography>
          </Stack>
          <Stack>
            <RuntimeStatusIndicator
              runtimeStatus={resource?.runtime_status}
              ingestionMode={resource?.ingestion_mode}
              status={resource?.status}
            />
          </Stack>
        </Stack>
      </Stack>
      <Stack className={styles.resumeSection}>
        <Stack>
          <Typography variant="subtitle2">Created on</Typography>
          <Typography variant="body2">
            {moment(created_at).format('MMM DD, YYYY [at] hh:mm A')}
          </Typography>
        </Stack>
      </Stack>
      {resourcesShowOwnerOrg.includes(resourceType) && (
        <>
          {owner && (
            <Stack className={styles.resumeSection}>
              <Stack>
                <Typography variant="subtitle2">Owner</Typography>
                <Typography variant="body2">
                  {owner?.full_name || owner?.name || owner?.email || 'Unknown'}
                  <br />({owner?.email})
                </Typography>
              </Stack>
            </Stack>
          )}
          {resourceOrg && (
            <Stack className={styles.resumeSection}>
              <Stack>
                <Typography variant="subtitle2">Organization</Typography>
                <Typography variant="body2">{resourceOrg?.name}</Typography>
              </Stack>
            </Stack>
          )}
        </>
      )}
      {resourcesShowButton.includes(resourceType) &&
        (resourceType === RESOURCE_TYPE_FLOW ? (
          <ViewExpandedPipelineButton
            title="Edit Flow"
            className={styles.buttonViewFlow}
            flowNodeId={id}
          />
        ) : (
          <Button
            className={styles.buttonViewFlow}
            variant="outlined"
            onClick={onShowModalFlowPreview}
          >
            Preview Flow
          </Button>
        ))}
    </Stack>
  );
};
